import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Plus, Trash2, Edit3, Save, X,
  FileText, Calendar, Filter, Search,
  ChevronDown, ChevronUp, Download, Loader2,
  Send, DollarSign, AlertTriangle, Receipt,
  CheckCircle, Clock, Eye, Upload, Sparkles
} from 'lucide-react';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { fmtEur } from '../utils/formatUtils';
import { useInvoicingData, getEffectiveStatus, getDueDays, getDueIndicator, STATUS_CONFIG } from '../hooks/useInvoicingData';
import { useInvoicingActions } from '../hooks/useInvoicingActions';

// ═════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════

export default function AdminInvoicing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const data = useInvoicingData();

  const actions = useInvoicingActions({
    userId: user?.id,
    projects: data.projects,
    filtered: data.filtered,
    formClientName: data.formClientName, setFormClientName: data.setFormClientName,
    formClientAddress: data.formClientAddress, setFormClientAddress: data.setFormClientAddress,
    formClientEmail: data.formClientEmail, setFormClientEmail: data.setFormClientEmail,
    formProjectId: data.formProjectId, setFormProjectId: data.setFormProjectId,
    formInvoiceDate: data.formInvoiceDate, setFormInvoiceDate: data.setFormInvoiceDate,
    formDueDate: data.formDueDate, setFormDueDate: data.setFormDueDate,
    formTvaRate: data.formTvaRate, setFormTvaRate: data.setFormTvaRate,
    formNotes: data.formNotes, setFormNotes: data.setFormNotes,
    formLines: data.formLines, setFormLines: data.setFormLines,
    editingId: data.editingId, setEditingId: data.setEditingId,
    saving: data.saving, setSaving: data.setSaving,
    showForm: data.showForm, setShowForm: data.setShowForm,
    setExpandedId: data.setExpandedId,
    pdfFile: data.pdfFile, setPdfFile: data.setPdfFile,
    setDragOver: data.setDragOver,
    setExtracting: data.setExtracting,
    setExtractionError: data.setExtractionError,
    signatureDetected: data.signatureDetected, setSignatureDetected: data.setSignatureDetected,
    setValidationMention: data.setValidationMention,
    resetForm: data.resetForm, computeDefaultDueDate: data.computeDefaultDueDate,
    fetchData: data.fetchData,
    dateStart: data.dateStart, dateEnd: data.dateEnd,
  });

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ─── HEADER ─────────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 50%, #5b21b6 100%)',
        borderRadius: '20px', padding: '28px 32px', marginBottom: '24px',
        boxShadow: '0 10px 30px -5px rgba(124,58,237,0.3)', color: 'white', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: '-40px', right: '-20px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', bottom: '-50px', left: '30%', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1, flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={() => navigate('/')} style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '12px',
              padding: '10px', cursor: 'pointer', color: 'white', display: 'flex'
            }}><ArrowLeft size={20} /></button>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: 'white' }}>
                <Receipt size={24} style={{ verticalAlign: 'middle', marginRight: '10px' }} />
                Facturation Client
              </h1>
              <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                Glissez un PDF ou créez manuellement
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button onClick={actions.exportExcel} style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '10px 18px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.1)', color: 'white',
              fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s'
            }}>
              <Download size={16} /> Export Excel
            </button>
            <button onClick={() => { data.resetForm(); data.setShowForm(true); }} style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '10px 18px', borderRadius: '12px', border: 'none',
              background: 'white', color: '#7c3aed',
              fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)', transition: 'all 0.2s'
            }}>
              <Plus size={16} /> Saisie manuelle
            </button>
          </div>
        </div>
      </div>

      {/* ─── KPI CARDS ──────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '20px' }}>
        {[
          { label: 'Total Facturé TTC', value: fmtEur(data.kpi.totalTTC), icon: Receipt, color: '#7c3aed', bg: '#f3e8ff' },
          { label: 'En attente', value: fmtEur(data.kpi.pending), icon: Clock, color: '#3b82f6', bg: '#eff6ff' },
          { label: 'Payé', value: fmtEur(data.kpi.paid), icon: CheckCircle, color: '#10b981', bg: '#ecfdf5' },
          { label: 'En retard', value: fmtEur(data.kpi.overdue), icon: AlertTriangle, color: '#ef4444', bg: '#fef2f2' },
        ].map((stat, i) => (
          <div key={i} style={{
            background: 'white', padding: '18px 20px', borderRadius: '16px',
            border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            display: 'flex', alignItems: 'center', gap: '14px', transition: 'all 0.2s'
          }}>
            <div style={{
              background: stat.bg, color: stat.color, padding: '10px', borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <stat.icon size={20} />
            </div>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#1e293b' }}>{stat.value}</div>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: '500' }}>{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── ZONE DRAG & DROP PDF ───────────────────────────────────────── */}
      {!data.showForm && (
        <div
          onDragOver={e => { e.preventDefault(); data.setDragOver(true); }}
          onDragLeave={() => data.setDragOver(false)}
          onDrop={actions.handleFileDrop}
          onClick={() => data.fileInputRef.current?.click()}
          onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.08)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; }}
          style={{
            background: data.dragOver ? '#f3e8ff' : 'linear-gradient(145deg, #ffffff, #f8fafc)',
            borderRadius: '20px',
            border: data.dragOver ? '2px dashed #7c3aed' : '2px dashed #cbd5e1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            textAlign: 'center' as const,
            padding: '40px 20px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginBottom: '20px'
          }}
        >
          {data.extracting ? (
            <div>
              <Loader2 size={36} color="#7c3aed" style={{ animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
              <p style={{ margin: '0 0 4px', fontWeight: '600', color: '#7c3aed', fontSize: '0.95rem' }}>
                Analyse en cours...
              </p>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.8rem' }}>
                {data.pdfFile?.name}
              </p>
            </div>
          ) : (
            <div>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: data.dragOver ? 'linear-gradient(135deg, #f3e8ff, #ddd6fe)' : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px', transition: 'all 0.2s'
              }}>
                <Upload size={28} color={data.dragOver ? '#7c3aed' : '#94a3b8'} />
              </div>
              <p style={{ margin: '0 0 4px', fontWeight: '600', color: '#475569', fontSize: '0.95rem' }}>
                Glissez une facture PDF ici
              </p>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.8rem' }}>
                ou cliquez pour sélectionner — Extraction automatique
              </p>
            </div>
          )}
          <input
            ref={data.fileInputRef}
            type="file"
            accept=".pdf"
            onChange={actions.handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Extraction error */}
      {data.extractionError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px',
          padding: '14px 18px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px'
        }}>
          <AlertTriangle size={18} color="#ef4444" />
          <span style={{ color: '#991b1b', fontSize: '0.9rem' }}>{data.extractionError}</span>
          <button onClick={() => data.setExtractionError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* ─── FORMULAIRE CREATION / EDITION ──────────────────────────────── */}
      {data.showForm && (
        <div style={{ ...cardStyle, marginBottom: '20px', borderLeft: '4px solid #7c3aed' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>
              {data.editingId ? <><Edit3 size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Modifier la facture</> : data.pdfFile ? <><FileText size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Facture extraite — vérifiez et enregistrez</> : <><Plus size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Nouvelle Facture</>}
            </h3>
            <button onClick={data.resetForm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
              <X size={20} />
            </button>
          </div>

          {/* Bandeau extraction réussie */}
          {data.pdfFile && !data.editingId && (
            <div style={{
              background: '#f3e8ff', border: '1px solid #ddd6fe', borderRadius: '10px',
              padding: '10px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px',
              fontSize: '0.85rem', color: '#6d28d9'
            }}>
              <Sparkles size={16} />
              <span><strong>Fichier analysé :</strong> {data.pdfFile.name} — Vérifiez les données puis cliquez "Enregistrer"</span>
            </div>
          )}

          {data.signatureDetected && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 16px', borderRadius: '10px',
              background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
              border: '1px solid #a7f3d0', marginBottom: '16px',
              fontSize: '0.85rem', color: '#065f46'
            }}>
              <CheckCircle size={16} color="#10b981" />
              <span>
                <strong>Signature détectée</strong>
                {data.validationMention ? ` — "${data.validationMention}"` : ''}
                {' → '}La facture sera créée comme <strong>Envoyée</strong>
              </span>
            </div>
          )}

          {/* Client info */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Nom du client *</label>
              <input value={data.formClientName} onChange={e => data.setFormClientName(e.target.value)} placeholder="Ex: SCI Les Tilleuls" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Adresse</label>
              <input value={data.formClientAddress} onChange={e => data.setFormClientAddress(e.target.value)} placeholder="Adresse complète" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email client</label>
              <input type="email" value={data.formClientEmail} onChange={e => data.setFormClientEmail(e.target.value)} placeholder="contact@client.fr" style={inputStyle} />
            </div>
          </div>

          {/* Invoice details */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Affaire (optionnel)</label>
              <select value={data.formProjectId} onChange={e => data.setFormProjectId(e.target.value)} style={inputStyle}>
                <option value="">— Facture libre —</option>
                {data.projects.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}><Calendar size={12} /> Date facture</label>
              <input type="date" value={data.formInvoiceDate} onChange={e => { data.setFormInvoiceDate(e.target.value); if (!data.formDueDate) data.setFormDueDate(data.computeDefaultDueDate(e.target.value)); }} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}><Calendar size={12} /> Échéance</label>
              <input type="date" value={data.formDueDate || data.computeDefaultDueDate(data.formInvoiceDate)} onChange={e => data.setFormDueDate(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>TVA (%)</label>
              <input type="number" step="0.1" value={data.formTvaRate} onChange={e => data.setFormTvaRate(parseFloat(e.target.value) || 0)} style={inputStyle} />
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={data.formNotes} onChange={e => data.setFormNotes(e.target.value)} rows={2}
              placeholder="Remarques, conditions particulières..."
              style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          {/* Lignes de facture */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ ...labelStyle, marginBottom: 0, fontWeight: '700' }}>Lignes de facture</label>
              <button onClick={data.addLine} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '6px 12px', borderRadius: '8px', border: '1px solid #cbd5e1',
                background: '#f8fafc', color: '#475569', fontSize: '0.8rem',
                cursor: 'pointer', fontWeight: '600'
              }}>
                <Plus size={14} /> Ajouter
              </button>
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 80px 70px 100px 100px 36px',
              gap: '8px', marginBottom: '6px', fontSize: '0.75rem', fontWeight: '700',
              color: '#64748b', textTransform: 'uppercase', padding: '0 4px'
            }}>
              <span>Description</span>
              <span style={{ textAlign: 'right' }}>Qté</span>
              <span>Unité</span>
              <span style={{ textAlign: 'right' }}>PU HT</span>
              <span style={{ textAlign: 'right' }}>Total HT</span>
              <span />
            </div>

            {data.formLines.map((line, idx) => (
              <div key={idx} style={{
                display: 'grid', gridTemplateColumns: '1fr 80px 70px 100px 100px 36px',
                gap: '8px', marginBottom: '6px', alignItems: 'center'
              }}>
                <input value={line.description} onChange={e => data.updateLine(idx, 'description', e.target.value)}
                  placeholder="Description..." style={{ ...inputStyle, padding: '8px 12px' }} />
                <input type="number" step="0.001" value={line.quantity} onChange={e => data.updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                  style={{ ...inputStyle, padding: '8px 12px', textAlign: 'right' }} />
                <select value={line.unit} onChange={e => data.updateLine(idx, 'unit', e.target.value)}
                  style={{ ...inputStyle, padding: '8px 6px' }}>
                  <option value="u">u</option>
                  <option value="m">m</option>
                  <option value="ml">ml</option>
                  <option value="m²">m²</option>
                  <option value="h">h</option>
                  <option value="j">j</option>
                  <option value="lot">lot</option>
                  <option value="forf.">forf.</option>
                </select>
                <input type="number" step="0.01" value={line.unit_price} onChange={e => data.updateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                  style={{ ...inputStyle, padding: '8px 12px', textAlign: 'right' }} />
                <div style={{ textAlign: 'right', fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>
                  {fmtEur(line.quantity * line.unit_price)}
                </div>
                <button onClick={() => data.removeLine(idx)} disabled={data.formLines.length <= 1}
                  style={{ background: 'none', border: 'none', cursor: data.formLines.length > 1 ? 'pointer' : 'not-allowed', color: data.formLines.length > 1 ? '#ef4444' : '#e2e8f0', padding: '4px' }}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          {/* Totaux live */}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: '24px', padding: '16px 0',
            borderTop: '2px solid #f1f5f9', marginBottom: '16px'
          }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: '600' }}>Total HT</div>
              <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{fmtEur(data.formTotalHT)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: '600' }}>TVA {data.formTvaRate}%</div>
              <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{fmtEur(data.formTVA)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: '600' }}>Total TTC</div>
              <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#7c3aed' }}>{fmtEur(data.formTotalTTC)}</div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={data.resetForm} style={secondaryBtnStyle}>
              <X size={16} /> Annuler
            </button>
            <button onClick={data.editingId ? actions.saveEdit : actions.handleCreate} disabled={data.saving} style={primaryBtnStyle}>
              {data.saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
              {data.editingId ? 'Enregistrer' : 'Créer la facture'}
            </button>
          </div>
        </div>
      )}

      {/* ─── FILTRES ────────────────────────────────────────────────────── */}
      <div style={{ ...cardStyle, marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <Filter size={16} color="#64748b" />
          <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.95rem' }}>Filtres</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
          <div>
            <label style={labelStyle}><Calendar size={12} /> Du</label>
            <input type="date" value={data.dateStart} onChange={e => data.setDateStart(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}><Calendar size={12} /> Au</label>
            <input type="date" value={data.dateEnd} onChange={e => data.setDateEnd(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Statut</label>
            <select value={data.filterStatus} onChange={e => data.setFilterStatus(e.target.value as any)} style={inputStyle}>
              <option value="all">Tous</option>
              <option value="draft">Brouillon</option>
              <option value="sent">Envoyée</option>
              <option value="paid">Payée</option>
              <option value="overdue">En retard</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Affaire</label>
            <select value={data.filterProject} onChange={e => data.setFilterProject(e.target.value)} style={inputStyle}>
              <option value="all">Toutes</option>
              {data.projects.filter(p => p.is_active).map(p => (
                <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}><Search size={12} /> Recherche</label>
            <input value={data.search} onChange={e => data.setSearch(e.target.value)} placeholder="N° facture, client..." style={inputStyle} />
          </div>
        </div>
      </div>

      {/* ─── LISTE DES FACTURES ─────────────────────────────────────────── */}
      {data.loading ? (
        <LoadingSpinner />
      ) : data.filtered.length === 0 ? (
        <EmptyState
          message="Aucune facture trouvée"
          icon={<Receipt size={48} />}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {data.filtered.map(inv => {
            const isExpanded = data.expandedId === inv.id;
            const effectiveStatus = getEffectiveStatus(inv);
            const sc = STATUS_CONFIG[effectiveStatus];

            return (
              <div key={inv.id}
                onMouseOver={e => { if (!isExpanded) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 20px 35px -10px rgba(0,0,0,0.1)'; } }}
                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0,0,0,0.05)'; }}
                style={{
                  ...cardStyle, borderRadius: '20px', borderLeft: `4px solid ${sc.color}`,
                  padding: 0, marginBottom: 0,
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03)',
                  transition: 'all 0.2s ease'
                }}>

                {/* Header */}
                <div onClick={() => data.handleExpand(inv.id)} style={{
                  padding: '16px 20px', cursor: 'pointer',
                  display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '12px', alignItems: 'center'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.95rem' }}>
                        {inv.invoice_number}
                      </span>
                      <span style={{ fontWeight: '600', color: '#475569', fontSize: '0.9rem' }}>
                        {inv.client_name}
                      </span>
                      {inv.project && (
                        <span style={{
                          background: '#f1f5f9', padding: '2px 8px', borderRadius: '6px',
                          fontSize: '0.8rem', color: '#475569', fontWeight: '600'
                        }}>
                          {inv.project.code}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '0.8rem', color: '#94a3b8' }}>
                      <span>{new Date(inv.invoice_date).toLocaleDateString('fr-FR')}</span>
                      <span>Éch. {new Date(inv.due_date).toLocaleDateString('fr-FR')}</span>
                      {effectiveStatus !== 'paid' && effectiveStatus !== 'draft' && (() => {
                        const days = getDueDays(inv.due_date);
                        const ind = getDueIndicator(days);
                        return (
                          <span style={{
                            fontSize: '0.7rem', fontWeight: '700', color: ind.color,
                            background: `${ind.color}15`, padding: '1px 8px', borderRadius: '8px'
                          }}>
                            {ind.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '700', fontSize: '1.1rem', color: '#1e293b' }}>
                      {fmtEur(inv.total_ttc)}
                    </div>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
                      fontSize: '0.75rem', fontWeight: '700', background: sc.bg, color: sc.color
                    }}>
                      {sc.label}
                    </span>
                  </div>
                  <div>
                    {isExpanded ? <ChevronUp size={20} color="#94a3b8" /> : <ChevronDown size={20} color="#94a3b8" />}
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: '16px 20px' }}>
                    {data.expandedLines.length > 0 && (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '16px' }}>
                        <thead>
                          <tr style={{ color: '#64748b', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                            <th style={thStyle}>Description</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Qté</th>
                            <th style={thStyle}>Unité</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>PU HT</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Total HT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.expandedLines.map(line => (
                            <tr key={line.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                              <td style={tdStyle}>{line.description}</td>
                              <td style={{ ...tdStyle, textAlign: 'right' }}>{line.quantity}</td>
                              <td style={tdStyle}>{line.unit}</td>
                              <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtEur(line.unit_price)}</td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>{fmtEur(line.total_ht)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <div style={{
                      display: 'flex', justifyContent: 'flex-end', gap: '20px', padding: '12px 0',
                      borderTop: '1px solid #f1f5f9', marginBottom: '16px'
                    }}>
                      <div style={summaryItemStyle}>
                        <span style={summaryLabelStyle}>Total HT</span>
                        <span style={{ fontWeight: '700', color: '#1e293b' }}>{fmtEur(inv.total_ht)}</span>
                      </div>
                      <div style={summaryItemStyle}>
                        <span style={summaryLabelStyle}>TVA {inv.tva_rate}%</span>
                        <span style={{ fontWeight: '700', color: '#1e293b' }}>{fmtEur(inv.total_ttc - inv.total_ht)}</span>
                      </div>
                      <div style={summaryItemStyle}>
                        <span style={summaryLabelStyle}>Total TTC</span>
                        <span style={{ fontWeight: '800', color: '#7c3aed', fontSize: '1.05rem' }}>{fmtEur(inv.total_ttc)}</span>
                      </div>
                    </div>

                    {inv.notes && (
                      <div style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: '10px', marginBottom: '16px', fontSize: '0.85rem', color: '#475569' }}>
                        <strong>Notes :</strong> {inv.notes}
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {effectiveStatus === 'draft' && (
                        <button onClick={() => actions.updateStatus(inv.id, 'sent')} style={{ ...actionBtnStyle, color: '#3b82f6', borderColor: '#bfdbfe' }}>
                          <Send size={14} /> Marquer envoyée
                        </button>
                      )}
                      {(effectiveStatus === 'sent' || effectiveStatus === 'overdue') && (
                        <button onClick={() => actions.updateStatus(inv.id, 'paid')} style={{ ...actionBtnStyle, color: '#10b981', borderColor: '#a7f3d0' }}>
                          <DollarSign size={14} /> Marquer payée
                        </button>
                      )}
                      {effectiveStatus === 'draft' && (
                        <button onClick={() => actions.startEdit(inv)} style={{ ...actionBtnStyle, color: '#f59e0b', borderColor: '#fde68a' }}>
                          <Edit3 size={14} /> Modifier
                        </button>
                      )}
                      {inv.pdf_url && (
                        <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" style={{ ...actionBtnStyle, color: '#0ea5e9', borderColor: '#7dd3fc', textDecoration: 'none' }}>
                          <Eye size={14} /> Voir PDF
                        </a>
                      )}
                      {effectiveStatus === 'draft' && (
                        <button onClick={() => actions.handleDelete(inv.id)} style={{ ...actionBtnStyle, color: '#ef4444', borderColor: '#fecaca' }}>
                          <Trash2 size={14} /> Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// STYLES
// ═════════════════════════════════════════════════════════════════════════

const cardStyle: React.CSSProperties = {
  background: 'white', borderRadius: '20px',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
  border: '1px solid #e2e8f0', padding: '24px', marginBottom: '16px'
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid #cbd5e1',
  fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
  background: 'white', transition: 'all 0.2s'
};

const labelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '6px',
  fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px'
};

const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  padding: '12px 22px', borderRadius: '12px', border: 'none',
  background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', color: 'white',
  fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer',
  boxShadow: '0 2px 4px rgba(124,58,237,0.2)', transition: 'all 0.2s ease'
};

const secondaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  padding: '12px 22px', borderRadius: '12px', border: '1px solid #cbd5e1',
  background: '#f1f5f9', color: '#475569',
  fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s ease'
};

const actionBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '8px 14px', borderRadius: '10px', border: '1px solid',
  background: 'white', fontWeight: '600', fontSize: '0.8rem',
  cursor: 'pointer', transition: 'all 0.2s'
};

const thStyle: React.CSSProperties = { padding: '10px 8px', fontWeight: '700', fontSize: '0.8rem' };
const tdStyle: React.CSSProperties = { padding: '10px 8px' };
const summaryItemStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '2px' };
const summaryLabelStyle: React.CSSProperties = { fontSize: '0.7rem', textTransform: 'uppercase', color: '#94a3b8', fontWeight: '600', letterSpacing: '0.05em' };
